import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { getSales, createSale, updateSale, deleteSale } from '../api/client';
import { useStores } from '../contexts/StoreContext';
import { useExchangeRate } from '../contexts/ExchangeRateContext';
import { formatCurrency, formatDualCurrency, formatDate, toLocalDateString } from '../utils/formatters';

const Sales = () => {
  const { stores, getStoreName } = useStores();
  const { exchangeRate } = useExchangeRate();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [editingSale, setEditingSale] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    store_id: '',
    amount_usd: '',
    amount_ves: '',
    cost_of_goods_sold: '',
    cost_currency: 'USD',
    date: new Date(),
    description: '',
    daily_transactions: '1',
    exchange_rate_used: '', // Campo para tasa de cambio personalizable
    payment_breakdown: {
      pos_banesco: '',
      pos_bdv: '',
      pos_bnc: '',
      pos_banplus: '',
      pos_provincial: '',
      cash_usd: '',
      cash_ves: '',
      zelle_transfer: '',
      biopago: '',
      cashea: '',
      pago_movil_banesco: '',
      pago_movil_bdv: '',
      pago_movil_bnc: '',
      pago_movil_banplus: '',
      credito: ''
    }
  });
  const [filters, setFilters] = useState({
    storeId: '',
    startDate: null,
    endDate: null,
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const salesData = await getSales(filters);
      setSales(salesData);
      setError(null);
    } catch (err) {
      console.error('Error fetching sales:', err);
      setError('Error al cargar las ventas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!loading) {
      fetchData();
    }
  }, [filters]);

  // Calculate totals from payment breakdown
  const calculateTotalsFromBreakdown = () => {
    const breakdown = formData.payment_breakdown;
    const currentExchangeRate = parseFloat(formData.exchange_rate_used) || exchangeRate;
    
    // VES amounts
    const vesTotal = 
      parseFloat(breakdown.pos_banesco || 0) +
      parseFloat(breakdown.pos_bdv || 0) +
      parseFloat(breakdown.pos_bnc || 0) +
      parseFloat(breakdown.pos_banplus || 0) +
      parseFloat(breakdown.pos_provincial || 0) +
      parseFloat(breakdown.cash_ves || 0) +
      parseFloat(breakdown.biopago || 0) +
      parseFloat(breakdown.cashea || 0) +
      parseFloat(breakdown.pago_movil_banesco || 0) +
      parseFloat(breakdown.pago_movil_bdv || 0) +
      parseFloat(breakdown.pago_movil_bnc || 0) +
      parseFloat(breakdown.pago_movil_banplus || 0) +
      parseFloat(breakdown.credito || 0);
    
    // USD amounts
    const usdTotal = parseFloat(breakdown.cash_usd || 0) + parseFloat(breakdown.zelle_transfer || 0);
    const usdToVes = usdTotal * currentExchangeRate;
    
    const totalVes = vesTotal + usdToVes;
    const totalUsd = totalVes / currentExchangeRate;
    
    return { totalVes, totalUsd, vesFromVes: vesTotal, vesFromUsd: usdToVes, usdAmount: usdTotal };
  };

  // Auto-calculate totals when breakdown changes
  useEffect(() => {
    if (showModal) {
      const totals = calculateTotalsFromBreakdown();
      setFormData(prev => ({
        ...prev,
        amount_ves: totals.totalVes.toFixed(2),
        amount_usd: totals.totalUsd.toFixed(2)
      }));
    }
  }, [formData.payment_breakdown, formData.exchange_rate_used, exchangeRate, showModal]);

  const handleOpenModal = () => {
    setFormData({
      store_id: '',
      amount_usd: '',
      amount_ves: '',
      cost_of_goods_sold: '',
      cost_currency: 'USD',
      date: new Date(),
      description: '',
      daily_transactions: '1',
      exchange_rate_used: exchangeRate.toFixed(2), // Inicializar con tasa actual
      payment_breakdown: {
        pos_banesco: '',
        pos_bdv: '',
        pos_banplus: '',
        pos_provincial: '',
        cash_usd: '',
        cash_ves: '',
        zelle_transfer: '',
        biopago: '',
        cashea: '',
        pago_movil_banesco: '',
        pago_movil_bdv: '',
        pago_movil_banplus: '',
        credito: ''
      }
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSale(null);
  };

  const handleViewDetails = (sale) => {
    setSelectedSale(sale);
    setShowDetailsModal(true);
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedSale(null);
  };

  const handleEditSale = (sale) => {
    // Set the sale being edited
    setEditingSale(sale);
    
    // Populate form with sale data
    setFormData({
      store_id: sale.store_id,
      amount_usd: sale.amount_usd.toString(),
      amount_ves: sale.amount_ves.toString(),
      cost_of_goods_sold: sale.cost_of_goods_sold_usd.toString(),
      cost_currency: 'USD', // Assume USD for existing sales
      date: new Date(sale.date),
      description: sale.description || '',
      daily_transactions: sale.daily_transactions?.toString() || '1',
      exchange_rate_used: sale.exchange_rate_used?.toString() || exchangeRate.toString(),
      payment_breakdown: sale.payment_breakdown || {
        pos_banesco: '',
        pos_bdv: '',
        pos_banplus: '',
        pos_provincial: '',
        cash_usd: '',
        cash_ves: '',
        zelle_transfer: '',
        biopago: '',
        cashea: '',
        pago_movil_banesco: '',
        pago_movil_bdv: '',
        pago_movil_banplus: '',
        credito: ''
      }
    });
    
    // Close details modal and open edit modal
    setShowDetailsModal(false);
    setShowModal(true);
  };

  const handleBreakdownChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      payment_breakdown: {
        ...prev.payment_breakdown,
        [field]: value
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Convert breakdown to numbers
      const cleanBreakdown = {};
      Object.keys(formData.payment_breakdown).forEach(key => {
        cleanBreakdown[key] = parseFloat(formData.payment_breakdown[key] || 0);
      });

      // Calculate cost in both currencies
      const costAmount = parseFloat(formData.cost_of_goods_sold || 0);
      const currentExchangeRate = parseFloat(formData.exchange_rate_used) || exchangeRate || 1;
      
      let cost_of_goods_sold_usd, cost_of_goods_sold_ves;
      if (formData.cost_currency === 'USD') {
        cost_of_goods_sold_usd = costAmount;
        cost_of_goods_sold_ves = costAmount * currentExchangeRate;
      } else {
        cost_of_goods_sold_ves = costAmount;
        cost_of_goods_sold_usd = costAmount / currentExchangeRate;
      }

      const saleData = {
        store_id: formData.store_id,
        amount_usd: parseFloat(formData.amount_usd),
        amount_ves: parseFloat(formData.amount_ves),
        exchange_rate_used: currentExchangeRate,
        cost_of_goods_sold_usd: parseFloat(cost_of_goods_sold_usd.toFixed(2)),
        cost_of_goods_sold_ves: parseFloat(cost_of_goods_sold_ves.toFixed(2)),
        cost_exchange_rate_used: currentExchangeRate,
        cost_currency: formData.cost_currency,
        daily_transactions: parseInt(formData.daily_transactions),
        date: toLocalDateString(formData.date),
        description: formData.description,
        payment_breakdown: cleanBreakdown
      };

      if (editingSale) {
        // Update existing sale
        await updateSale(editingSale.id, saleData);
      } else {
        // Create new sale
        await createSale(saleData);
      }
      
      await fetchData();
      handleCloseModal();
    } catch (err) {
      console.error('Error processing sale:', err);
      const action = editingSale ? 'actualizar' : 'registrar';
      
      // Better error message handling
      let errorMessage = `Error al ${action} la venta: `;
      
      if (err.response?.data?.detail) {
        errorMessage += err.response.data.detail;
      } else if (err.response?.data?.message) {
        errorMessage += err.response.data.message;
      } else if (err.message) {
        errorMessage += err.message;
      } else if (typeof err === 'string') {
        errorMessage += err;
      } else {
        errorMessage += 'Error desconocido';
      }
      
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (saleId) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar esta venta?')) {
      try {
        await deleteSale(saleId);
        await fetchData();
      } catch (err) {
        console.error('Error deleting sale:', err);
        setError('Error al eliminar la venta');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
        <span className="ml-2 text-gray-600">Cargando ventas...</span>
      </div>
    );
  }

  const totals = calculateTotalsFromBreakdown();

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">💰 Ventas</h1>
          <p className="text-gray-600 mt-1">
            Registro de ventas diarias con desglose de métodos de pago
          </p>
        </div>
        <button onClick={handleOpenModal} className="btn-primary">
          ➕ Registrar Venta
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => setError(null)} className="text-sm underline ml-2">
            Cerrar
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">🔍 Filtros</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="form-label">Tienda</label>
            <select
              value={filters.storeId}
              onChange={(e) => setFilters(prev => ({ ...prev, storeId: e.target.value }))}
              className="form-select"
            >
              <option value="">Todas las tiendas</option>
              {stores.map(store => (
                <option key={store.id} value={store.id}>{store.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label">Fecha Desde</label>
            <DatePicker
              selected={filters.startDate}
              onChange={(date) => setFilters(prev => ({ ...prev, startDate: date }))}
              className="form-input"
              placeholderText="Fecha inicio"
              dateFormat="dd/MM/yyyy"
              isClearable
            />
          </div>
          <div>
            <label className="form-label">Fecha Hasta</label>
            <DatePicker
              selected={filters.endDate}
              onChange={(date) => setFilters(prev => ({ ...prev, endDate: date }))}
              className="form-input"
              placeholderText="Fecha fin"
              dateFormat="dd/MM/yyyy"
              isClearable
              minDate={filters.startDate}
            />
          </div>
        </div>
      </div>

      {/* Sales List */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">📊 Lista de Ventas</h3>
          <span className="text-sm text-gray-600">
            {sales.length} venta{sales.length !== 1 ? 's' : ''} registrada{sales.length !== 1 ? 's' : ''}
          </span>
        </div>

        {sales.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">💰</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No hay ventas registradas
            </h3>
            <p className="text-gray-600 mb-4">
              Registra tu primera venta para comenzar a trackear tus ingresos.
            </p>
            <button onClick={handleOpenModal} className="btn-primary">
              ➕ Registrar Primera Venta
            </button>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Tienda</th>
                  <th>Monto Total</th>
                  <th>Costo</th>
                  <th>Ganancia</th>
                  <th>Margen %</th>
                  <th>Transacciones</th>
                  <th>Descripción</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {sales.map((sale) => (
                  <tr key={sale.id}>
                    <td>
                      <span className="font-medium">
                        {formatDate(sale.date)}
                      </span>
                    </td>
                    <td>
                      <span className="text-blue-600 font-medium">
                        {getStoreName(sale.store_id)}
                      </span>
                    </td>
                    <td>
                      <div className="font-semibold">
                        {sale.amount_usd && sale.amount_ves ? (
                          <div>
                            <div className="text-green-600">${sale.amount_usd?.toFixed(2)}</div>
                            <div className="text-blue-600 text-sm">Bs. {sale.amount_ves?.toFixed(2)}</div>
                          </div>
                        ) : (
                          <span className="text-green-600">{formatCurrency(sale.amount || 0)}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="text-sm">
                        {sale.cost_of_goods_sold_usd !== undefined && sale.cost_of_goods_sold_ves !== undefined ? (
                          <div>
                            <div className="text-red-600">${sale.cost_of_goods_sold_usd?.toFixed(2)}</div>
                            <div className="text-red-500 text-xs">Bs. {sale.cost_of_goods_sold_ves?.toFixed(2)}</div>
                          </div>
                        ) : (
                          <span className="text-gray-500 text-xs">No registrado</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="text-sm">
                        {sale.profit_usd !== undefined && sale.profit_ves !== undefined ? (
                          <div>
                            <div className={`font-semibold ${sale.profit_usd >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              ${sale.profit_usd?.toFixed(2)}
                            </div>
                            <div className={`text-xs ${sale.profit_ves >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                              Bs. {sale.profit_ves?.toFixed(2)}
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-500 text-xs">No calculado</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="text-center">
                        {(() => {
                          // Calculate margin dynamically using current formula (margin over cost)
                          const profit = sale.amount_usd - sale.cost_of_goods_sold_usd;
                          const dynamicMargin = sale.cost_of_goods_sold_usd > 0 ? (profit / sale.cost_of_goods_sold_usd * 100) : 0;
                          
                          return (
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              dynamicMargin >= 50 ? 'bg-green-100 text-green-800' :
                              dynamicMargin >= 20 ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {dynamicMargin.toFixed(1)}%
                            </span>
                          );
                        })()}
                      </div>
                    </td>
                    <td>
                      <div className="text-center">
                        <span className="text-gray-700 font-medium">
                          {sale.daily_transactions || 1}
                        </span>
                        <div className="text-xs text-gray-500">
                          {sale.average_ticket_usd ? (
                            <span>Ticket: ${sale.average_ticket_usd?.toFixed(2)}</span>
                          ) : (
                            <span>Ticket: {formatCurrency((sale.amount || 0) / (sale.daily_transactions || 1))}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="text-gray-600 text-sm">
                        {sale.description || '-'}
                      </span>
                    </td>
                    <td>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleViewDetails(sale)}
                          className="btn-primary text-sm"
                          title="Ver detalles completos de la venta"
                        >
                          👁️ Ver Detalles
                        </button>
                        <button
                          onClick={() => handleDelete(sale.id)}
                          className="btn-outline-danger text-sm"
                        >
                          🗑️ Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Sales Registration Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editingSale ? '✏️ Editar Venta' : '💰 Registrar Venta Diaria'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="form-label">Tienda *</label>
                    <select
                      value={formData.store_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, store_id: e.target.value }))}
                      className="form-select"
                      required
                    >
                      <option value="">Seleccionar tienda</option>
                      {stores.map(store => (
                        <option key={store.id} value={store.id}>{store.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="form-label">Fecha *</label>
                    <DatePicker
                      selected={formData.date}
                      onChange={(date) => setFormData(prev => ({ ...prev, date }))}
                      className="form-input"
                      dateFormat="dd/MM/yyyy"
                      required
                    />
                  </div>

                  <div>
                    <label className="form-label">
                      Tasa de Cambio * 
                      <span className="text-xs text-gray-500 block">(Bs./USD)</span>
                    </label>
                    <div className="space-y-2">
                      <input
                        type="number"
                        value={formData.exchange_rate_used}
                        onChange={(e) => setFormData(prev => ({ ...prev, exchange_rate_used: e.target.value }))}
                        className="form-input w-full"
                        step="0.01"
                        min="0.01"
                        required
                        placeholder="166.58"
                      />
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, exchange_rate_used: exchangeRate.toFixed(2) }))}
                        className="w-full px-3 py-1 text-xs bg-blue-100 text-blue-600 rounded hover:bg-blue-200 transition-colors border border-blue-300"
                        title="Usar tasa actual"
                      >
                        Usar Tasa Actual ({exchangeRate.toFixed(2)})
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="form-label">Número de Transacciones *</label>
                    <input
                      type="number"
                      value={formData.daily_transactions}
                      onChange={(e) => setFormData(prev => ({ ...prev, daily_transactions: e.target.value }))}
                      className="form-input"
                      min="1"
                      required
                    />
                  </div>
                </div>

                {/* Payment Breakdown */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-4">💳 Desglose de Métodos de Pago</h4>
                  
                  {/* POS Section */}
                  <div className="mb-6">
                    <h5 className="font-medium text-blue-900 mb-3">🏦 Puntos de Venta</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <label className="form-label">POS Banesco (Bs.)</label>
                        <input
                          type="number"
                          value={formData.payment_breakdown.pos_banesco}
                          onChange={(e) => handleBreakdownChange('pos_banesco', e.target.value)}
                          className="form-input"
                          step="0.01"
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <label className="form-label">POS BDV (Bs.)</label>
                        <input
                          type="number"
                          value={formData.payment_breakdown.pos_bdv}
                          onChange={(e) => handleBreakdownChange('pos_bdv', e.target.value)}
                          className="form-input"
                          step="0.01"
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <label className="form-label">POS BNC (Bs.)</label>
                        <input
                          type="number"
                          value={formData.payment_breakdown.pos_bnc}
                          onChange={(e) => handleBreakdownChange('pos_bnc', e.target.value)}
                          className="form-input"
                          step="0.01"
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <label className="form-label">POS Banplus (Bs.)</label>
                        <input
                          type="number"
                          value={formData.payment_breakdown.pos_banplus}
                          onChange={(e) => handleBreakdownChange('pos_banplus', e.target.value)}
                          className="form-input"
                          step="0.01"
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <label className="form-label">POS Provincial (Bs.)</label>
                        <input
                          type="number"
                          value={formData.payment_breakdown.pos_provincial}
                          onChange={(e) => handleBreakdownChange('pos_provincial', e.target.value)}
                          className="form-input"
                          step="0.01"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Cash Section */}
                  <div className="mb-6">
                    <h5 className="font-medium text-green-900 mb-3">💵 Efectivo</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="form-label">Dólares Efectivo ($)</label>
                        <input
                          type="number"
                          value={formData.payment_breakdown.cash_usd}
                          onChange={(e) => handleBreakdownChange('cash_usd', e.target.value)}
                          className="form-input"
                          step="0.01"
                          placeholder="0.00"
                        />
                        {formData.payment_breakdown.cash_usd && (
                          <p className="text-xs text-green-600 mt-1">
                            = Bs. {(parseFloat(formData.payment_breakdown.cash_usd || 0) * (parseFloat(formData.exchange_rate_used) || exchangeRate)).toFixed(2)}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="form-label">Bolívares Efectivo (Bs.)</label>
                        <input
                          type="number"
                          value={formData.payment_breakdown.cash_ves}
                          onChange={(e) => handleBreakdownChange('cash_ves', e.target.value)}
                          className="form-input"
                          step="0.01"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Digital Payments Section */}
                  <div className="mb-6">
                    <h5 className="font-medium text-purple-900 mb-3">📱 Pagos Digitales</h5>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="form-label">Zelle/Transferencia Int. ($)</label>
                        <input
                          type="number"
                          value={formData.payment_breakdown.zelle_transfer}
                          onChange={(e) => handleBreakdownChange('zelle_transfer', e.target.value)}
                          className="form-input"
                          step="0.01"
                          placeholder="0.00"
                        />
                        {formData.payment_breakdown.zelle_transfer && (
                          <p className="text-xs text-purple-600 mt-1">
                            = Bs. {(parseFloat(formData.payment_breakdown.zelle_transfer || 0) * (parseFloat(formData.exchange_rate_used) || exchangeRate)).toFixed(2)}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="form-label">Biopago (Bs.)</label>
                        <input
                          type="number"
                          value={formData.payment_breakdown.biopago}
                          onChange={(e) => handleBreakdownChange('biopago', e.target.value)}
                          className="form-input"
                          step="0.01"
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <label className="form-label">Cashea (Bs.)</label>
                        <input
                          type="number"
                          value={formData.payment_breakdown.cashea}
                          onChange={(e) => handleBreakdownChange('cashea', e.target.value)}
                          className="form-input"
                          step="0.01"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Mobile Payments Section */}
                  <div className="mb-6">
                    <h5 className="font-medium text-orange-900 mb-3">📲 Pago Móvil</h5>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="form-label">Pago Móvil Banesco (Bs.)</label>
                        <input
                          type="number"
                          value={formData.payment_breakdown.pago_movil_banesco}
                          onChange={(e) => handleBreakdownChange('pago_movil_banesco', e.target.value)}
                          className="form-input"
                          step="0.01"
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <label className="form-label">Pago Móvil BDV (Bs.)</label>
                        <input
                          type="number"
                          value={formData.payment_breakdown.pago_movil_bdv}
                          onChange={(e) => handleBreakdownChange('pago_movil_bdv', e.target.value)}
                          className="form-input"
                          step="0.01"
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <label className="form-label">Pago Móvil BNC (Bs.)</label>
                        <input
                          type="number"
                          value={formData.payment_breakdown.pago_movil_bnc}
                          onChange={(e) => handleBreakdownChange('pago_movil_bnc', e.target.value)}
                          className="form-input"
                          step="0.01"
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <label className="form-label">Pago Móvil Banplus (Bs.)</label>
                        <input
                          type="number"
                          value={formData.payment_breakdown.pago_movil_banplus}
                          onChange={(e) => handleBreakdownChange('pago_movil_banplus', e.target.value)}
                          className="form-input"
                          step="0.01"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Credit Section */}
                  <div className="mb-6">
                    <h5 className="font-medium text-orange-900 mb-3">💳 Crédito</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="form-label">Crédito (Bs.)</label>
                        <input
                          type="number"
                          value={formData.payment_breakdown.credito}
                          onChange={(e) => handleBreakdownChange('credito', e.target.value)}
                          className="form-input"
                          step="0.01"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Totals Summary */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-3">📊 Resumen de Totales</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">Total en USD</label>
                      <input
                        type="number"
                        value={formData.amount_usd}
                        onChange={(e) => setFormData(prev => ({ ...prev, amount_usd: e.target.value }))}
                        className="form-input bg-white"
                        step="0.01"
                        required
                        readOnly
                      />
                    </div>
                    <div>
                      <label className="form-label">Total en VES</label>
                      <input
                        type="number"
                        value={formData.amount_ves}
                        onChange={(e) => setFormData(prev => ({ ...prev, amount_ves: e.target.value }))}
                        className="form-input bg-white"
                        step="0.01"
                        required
                        readOnly
                      />
                    </div>
                  </div>
                  
                  {formData.daily_transactions && formData.amount_usd && (
                    <div className="mt-4 p-3 bg-white rounded border">
                      <p className="text-sm text-gray-700">
                        <strong>Ticket Promedio:</strong> ${(parseFloat(formData.amount_usd) / parseInt(formData.daily_transactions)).toFixed(2)} USD / 
                        Bs. {(parseFloat(formData.amount_ves) / parseInt(formData.daily_transactions)).toFixed(2)} VES
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Tasa utilizada: Bs. {(parseFloat(formData.exchange_rate_used) || exchangeRate).toFixed(2)} por $1 USD
                      </p>
                    </div>
                  )}
                </div>

                {/* Cost of Goods Sold */}
                <div className="bg-orange-50 p-4 rounded-lg">
                  <h4 className="font-medium text-orange-900 mb-3">💰 Costo de lo Vendido</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="form-label">Moneda del Costo</label>
                      <select
                        value={formData.cost_currency}
                        onChange={(e) => setFormData(prev => ({ ...prev, cost_currency: e.target.value }))}
                        className="form-select"
                        required
                      >
                        <option value="USD">USD ($)</option>
                        <option value="VES">VES (Bs.)</option>
                      </select>
                    </div>
                    <div className="md:col-span-2">
                      <label className="form-label">Costo Total del Día</label>
                      <input
                        type="number"
                        value={formData.cost_of_goods_sold}
                        onChange={(e) => setFormData(prev => ({ ...prev, cost_of_goods_sold: e.target.value }))}
                        className="form-input"
                        step="0.01"
                        required
                        placeholder={formData.cost_currency === 'USD' ? '850.00' : '141,593.00'}
                      />
                      {formData.cost_of_goods_sold && formData.exchange_rate_used && (
                        <div className="mt-1 text-sm text-gray-600">
                          {formData.cost_currency === 'USD' ? (
                            <span>≈ Bs. {(parseFloat(formData.cost_of_goods_sold || 0) * (parseFloat(formData.exchange_rate_used) || exchangeRate)).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          ) : (
                            <span>≈ ${(parseFloat(formData.cost_of_goods_sold || 0) / (parseFloat(formData.exchange_rate_used) || exchangeRate)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Profit Calculation Preview */}
                  {formData.amount_usd && formData.cost_of_goods_sold && (
                    <div className="mt-4 p-3 bg-white rounded border">
                      {(() => {
                        const costAmount = parseFloat(formData.cost_of_goods_sold || 0);
                        const currentExchangeRate = parseFloat(formData.exchange_rate_used) || exchangeRate || 1;
                        
                        let cost_usd, cost_ves;
                        if (formData.cost_currency === 'USD') {
                          cost_usd = costAmount;
                          cost_ves = costAmount * currentExchangeRate;
                        } else {
                          cost_ves = costAmount;
                          cost_usd = costAmount / currentExchangeRate;
                        }
                        
                        const saleAmountUsd = parseFloat(formData.amount_usd);
                        const saleAmountVes = parseFloat(formData.amount_ves);
                        const profitUsd = saleAmountUsd - cost_usd;
                        const profitVes = saleAmountVes - cost_ves;
                        const profitMargin = cost_usd > 0 ? (profitUsd / cost_usd * 100) : 0;
                        
                        return (
                          <>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <p className="text-sm text-gray-700">
                                  <strong>Ganancia:</strong> ${profitUsd.toFixed(2)} USD
                                </p>
                                <p className="text-xs text-gray-500">
                                  Bs. {profitVes.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} VES
                                </p>
                              </div>
                              <div>
                                <p className={`text-sm font-semibold ${profitMargin >= 50 ? 'text-green-700' : profitMargin >= 20 ? 'text-yellow-700' : 'text-red-700'}`}>
                                  <strong>Margen:</strong> {profitMargin.toFixed(1)}%
                                </p>
                                <p className="text-xs text-gray-500">
                                  {profitMargin >= 50 ? '✅ Excelente' : profitMargin >= 20 ? '⚠️ Aceptable' : '🚨 Bajo'}
                                </p>
                              </div>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="form-label">Descripción / Notas</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    className="form-textarea"
                    rows="3"
                    placeholder="Notas adicionales sobre la venta del día..."
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="btn-outline"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary"
                >
                  {submitting ? (
                    <span className="flex items-center">
                      <div className="loading-spinner mr-2"></div>
                      {editingSale ? 'Actualizando...' : 'Registrando...'}
                    </span>
                  ) : (
                    editingSale ? '✏️ Actualizar Venta' : '💰 Registrar Venta'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Sale Details Modal */}
      {showDetailsModal && selectedSale && (
        <div className="modal-overlay" onClick={handleCloseDetailsModal}>
          <div className="modal-content max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">👁️ Detalles de Venta Completos</h2>
              <button
                onClick={handleCloseDetailsModal}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              {/* Basic Information */}
              <div className="bg-blue-50 p-4 rounded-lg mb-6">
                <h4 className="font-medium text-blue-900 mb-3">📋 Información General</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <span className="text-sm font-medium text-blue-700">ID de Venta:</span>
                    <p className="text-blue-900 font-mono text-sm">{selectedSale.id}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-blue-700">Fecha de Venta:</span>
                    <p className="text-blue-900 font-semibold">{formatDate(selectedSale.date)}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-blue-700">Tienda:</span>
                    <p className="text-blue-900 font-semibold">{getStoreName(selectedSale.store_id)}</p>
                  </div>
                </div>
                {selectedSale.description && (
                  <div className="mt-3">
                    <span className="text-sm font-medium text-blue-700">Descripción:</span>
                    <p className="text-blue-900 mt-1">{selectedSale.description}</p>
                  </div>
                )}
              </div>

              {/* Sales Amounts */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-medium text-green-900 mb-3">💰 Montos de Venta</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-green-700">Monto USD:</span>
                      <span className="font-bold text-green-800">${selectedSale.amount_usd?.toFixed(2) || selectedSale.amount?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-green-700">Monto VES:</span>
                      <span className="font-bold text-green-800">Bs. {selectedSale.amount_ves?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-green-200">
                      <span className="text-green-700">Tasa de Cambio:</span>
                      <span className="font-semibold text-green-800">{selectedSale.exchange_rate_used?.toFixed(2) || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-red-50 p-4 rounded-lg">
                  <h4 className="font-medium text-red-900 mb-3">📦 Costo de lo Vendido</h4>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-red-700">Costo USD:</span>
                      <span className="font-bold text-red-800">${selectedSale.cost_of_goods_sold_usd?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-red-700">Costo VES:</span>
                      <span className="font-bold text-red-800">Bs. {selectedSale.cost_of_goods_sold_ves?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-red-200">
                      <span className="text-red-700">Moneda del Costo:</span>
                      <span className="font-semibold text-red-800">{selectedSale.cost_currency || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Profit Information */}
              <div className="bg-blue-50 p-4 rounded-lg mb-6">
                <h4 className="font-medium text-blue-900 mb-3">📈 Análisis de Ganancia</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <span className="text-sm font-medium text-blue-700">Ganancia USD:</span>
                    <p className="text-xl font-bold text-blue-800">${selectedSale.profit_usd?.toFixed(2) || '0.00'}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-blue-700">Ganancia VES:</span>
                    <p className="text-xl font-bold text-blue-800">Bs. {selectedSale.profit_ves?.toFixed(2) || '0.00'}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-blue-700">Margen de Ganancia:</span>
                    <p className={`text-xl font-bold ${
                      (() => {
                        const profit = selectedSale.amount_usd - selectedSale.cost_of_goods_sold_usd;
                        const dynamicMargin = selectedSale.cost_of_goods_sold_usd > 0 ? (profit / selectedSale.cost_of_goods_sold_usd * 100) : 0;
                        return dynamicMargin >= 50 ? 'text-green-600' :
                               dynamicMargin >= 20 ? 'text-yellow-600' : 
                               'text-red-600';
                      })()
                    }`}>
                      {(() => {
                        const profit = selectedSale.amount_usd - selectedSale.cost_of_goods_sold_usd;
                        const dynamicMargin = selectedSale.cost_of_goods_sold_usd > 0 ? (profit / selectedSale.cost_of_goods_sold_usd * 100) : 0;
                        return dynamicMargin.toFixed(1);
                      })()}%
                    </p>
                  </div>
                </div>
              </div>

              {/* Transaction Details */}
              <div className="bg-purple-50 p-4 rounded-lg mb-6">
                <h4 className="font-medium text-purple-900 mb-3">🧾 Detalles de Transacciones</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <span className="text-sm font-medium text-purple-700">Número de Transacciones:</span>
                    <p className="text-lg font-bold text-purple-800">{selectedSale.daily_transactions || 1}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-purple-700">Ticket Promedio USD:</span>
                    <p className="text-lg font-bold text-purple-800">${selectedSale.average_ticket_usd?.toFixed(2) || '0.00'}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-purple-700">Ticket Promedio VES:</span>
                    <p className="text-lg font-bold text-purple-800">Bs. {selectedSale.average_ticket_ves?.toFixed(2) || '0.00'}</p>
                  </div>
                </div>
              </div>

              {/* Payment Breakdown */}
              {selectedSale.payment_breakdown && Object.keys(selectedSale.payment_breakdown).length > 0 && (
                <div className="bg-gray-50 p-4 rounded-lg mb-6">
                  <h4 className="font-medium text-gray-900 mb-3">💳 Desglose de Métodos de Pago</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Cash Payments */}
                    <div>
                      <h5 className="font-medium text-gray-700 mb-2">💵 Efectivo</h5>
                      <div className="space-y-1 text-sm">
                        {selectedSale.payment_breakdown.cash_usd > 0 && (
                          <div className="flex justify-between">
                            <span>Efectivo USD:</span>
                            <span className="font-semibold">${selectedSale.payment_breakdown.cash_usd.toFixed(2)}</span>
                          </div>
                        )}
                        {selectedSale.payment_breakdown.cash_ves > 0 && (
                          <div className="flex justify-between">
                            <span>Efectivo VES:</span>
                            <span className="font-semibold">Bs. {selectedSale.payment_breakdown.cash_ves.toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Electronic Payments */}
                    <div>
                      <h5 className="font-medium text-gray-700 mb-2">🏦 Transferencias y POS</h5>
                      <div className="space-y-1 text-sm">
                        {selectedSale.payment_breakdown.zelle_transfer > 0 && (
                          <div className="flex justify-between">
                            <span>Zelle:</span>
                            <span className="font-semibold">${selectedSale.payment_breakdown.zelle_transfer.toFixed(2)}</span>
                          </div>
                        )}
                        {selectedSale.payment_breakdown.pos_banesco > 0 && (
                          <div className="flex justify-between">
                            <span>POS Banesco:</span>
                            <span className="font-semibold">Bs. {selectedSale.payment_breakdown.pos_banesco.toFixed(2)}</span>
                          </div>
                        )}
                        {selectedSale.payment_breakdown.pos_bdv > 0 && (
                          <div className="flex justify-between">
                            <span>POS BDV:</span>
                            <span className="font-semibold">Bs. {selectedSale.payment_breakdown.pos_bdv.toFixed(2)}</span>
                          </div>
                        )}
                        {selectedSale.payment_breakdown.pos_bnc > 0 && (
                          <div className="flex justify-between">
                            <span>POS BNC:</span>
                            <span className="font-semibold">Bs. {selectedSale.payment_breakdown.pos_bnc.toFixed(2)}</span>
                          </div>
                        )}
                        {selectedSale.payment_breakdown.pos_banplus > 0 && (
                          <div className="flex justify-between">
                            <span>POS Banplus:</span>
                            <span className="font-semibold">Bs. {selectedSale.payment_breakdown.pos_banplus.toFixed(2)}</span>
                          </div>
                        )}
                        {selectedSale.payment_breakdown.pos_provincial > 0 && (
                          <div className="flex justify-between">
                            <span>POS Provincial:</span>
                            <span className="font-semibold">Bs. {selectedSale.payment_breakdown.pos_provincial.toFixed(2)}</span>
                          </div>
                        )}
                        {selectedSale.payment_breakdown.pago_movil_banesco > 0 && (
                          <div className="flex justify-between">
                            <span>Pago Móvil Banesco:</span>
                            <span className="font-semibold">Bs. {selectedSale.payment_breakdown.pago_movil_banesco.toFixed(2)}</span>
                          </div>
                        )}
                        {selectedSale.payment_breakdown.pago_movil_bdv > 0 && (
                          <div className="flex justify-between">
                            <span>Pago Móvil BDV:</span>
                            <span className="font-semibold">Bs. {selectedSale.payment_breakdown.pago_movil_bdv.toFixed(2)}</span>
                          </div>
                        )}
                        {selectedSale.payment_breakdown.pago_movil_bnc > 0 && (
                          <div className="flex justify-between">
                            <span>Pago Móvil BNC:</span>
                            <span className="font-semibold">Bs. {selectedSale.payment_breakdown.pago_movil_bnc.toFixed(2)}</span>
                          </div>
                        )}
                        {selectedSale.payment_breakdown.pago_movil_banplus > 0 && (
                          <div className="flex justify-between">
                            <span>Pago Móvil Banplus:</span>
                            <span className="font-semibold">Bs. {selectedSale.payment_breakdown.pago_movil_banplus.toFixed(2)}</span>
                          </div>
                        )}
                        {selectedSale.payment_breakdown.biopago > 0 && (
                          <div className="flex justify-between">
                            <span>Biopago:</span>
                            <span className="font-semibold">Bs. {selectedSale.payment_breakdown.biopago.toFixed(2)}</span>
                          </div>
                        )}
                        {selectedSale.payment_breakdown.cashea > 0 && (
                          <div className="flex justify-between">
                            <span>Cashea:</span>
                            <span className="font-semibold">Bs. {selectedSale.payment_breakdown.cashea.toFixed(2)}</span>
                          </div>
                        )}
                        {selectedSale.payment_breakdown.credito > 0 && (
                          <div className="flex justify-between">
                            <span>Crédito:</span>
                            <span className="font-semibold">Bs. {selectedSale.payment_breakdown.credito.toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Metadata */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-3">🔍 Información del Sistema</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">Fecha de Creación:</span>
                    <p className="text-gray-600">{selectedSale.created_at ? formatDate(selectedSale.created_at) : 'N/A'}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">ID de la Tienda:</span>
                    <p className="text-gray-600 font-mono text-xs">{selectedSale.store_id}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={handleCloseDetailsModal}
                className="btn-outline"
              >
                Cerrar Detalles
              </button>
              <button
                onClick={() => handleEditSale(selectedSale)}
                className="btn-primary"
              >
                ✏️ Editar Venta
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sales;